import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore } from '../store/gameStore';
import { ArrowLeft, Timer, Target, Zap } from 'lucide-react';

interface ColorBlock {
  id: number;
  color: 'red' | 'green';
  x: number;
  y: number;
  size: number;
  visible: boolean;
}

export default function ColorIdentifyLevel() {
  const navigate = useNavigate();
  const { contrastLevel, speedLevel, addEnergy, completeLevel, unlockLevel } = useGameStore();
  const [blocks, setBlocks] = useState<ColorBlock[]>([]);
  const [targetColor, setTargetColor] = useState<'red' | 'green'>('red');
  const [score, setScore] = useState(0);
  const [round, setRound] = useState(0);
  const [totalRounds] = useState(15);
  const [gameState, setGameState] = useState<'ready' | 'playing' | 'complete'>('ready');
  const [reactionTimes, setReactionTimes] = useState<number[]>([]);
  const [roundStartTime, setRoundStartTime] = useState(0);
  const [timeLeft, setTimeLeft] = useState(45);
  const [hits, setHits] = useState(0);
  const [misses, setMisses] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'correct' | 'wrong'; x: number; y: number } | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const spawnRef = useRef<NodeJS.Timeout | null>(null);

  const spawnBlocks = useCallback(() => {
    const count = 6 + Math.floor(round / 3);
    const newBlocks: ColorBlock[] = [];
    const target: 'red' | 'green' = Math.random() > 0.5 ? 'red' : 'green';
    setTargetColor(target);

    for (let i = 0; i < count; i++) {
      newBlocks.push({
        id: i,
        color: i === 0 ? target : (Math.random() > 0.5 ? 'red' : 'green'),
        x: Math.random() * 75 + 5,
        y: Math.random() * 70 + 10,
        size: Math.random() * 20 + 30,
        visible: true,
      });
    }
    // Shuffle
    for (let i = newBlocks.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newBlocks[i], newBlocks[j]] = [newBlocks[j], newBlocks[i]];
    }
    setBlocks(newBlocks);
    setRoundStartTime(Date.now());
  }, [round]);

  const startGame = () => {
    setGameState('playing');
    setScore(0);
    setRound(0);
    setHits(0);
    setMisses(0);
    setReactionTimes([]);
    setTimeLeft(45);
    spawnBlocks();
  };

  useEffect(() => {
    if (gameState === 'playing') {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setGameState('complete');
            setShowResult(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Auto-move blocks
      spawnRef.current = setInterval(() => {
        setBlocks(prev => prev.map(b => ({
          ...b,
          x: Math.max(5, Math.min(75, b.x + (Math.random() - 0.5) * 8 * speedLevel)),
          y: Math.max(10, Math.min(75, b.y + (Math.random() - 0.5) * 8 * speedLevel)),
        })));
      }, 1200 / speedLevel);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (spawnRef.current) clearInterval(spawnRef.current);
    };
  }, [gameState, speedLevel]);

  const handleBlockClick = (block: ColorBlock) => {
    if (gameState !== 'playing') return;

    const reactionTime = Date.now() - roundStartTime;
    setReactionTimes(prev => [...prev, reactionTime]);
    setFeedback({ type: block.color === targetColor ? 'correct' : 'wrong', x: block.x, y: block.y });
    setTimeout(() => setFeedback(null), 500);

    if (block.color === targetColor) {
      setScore(s => s + 15);
      setHits(h => h + 1);
      setRound(r => {
        const newRound = r + 1;
        if (newRound >= totalRounds) {
          setGameState('complete');
          setShowResult(true);
        } else {
          spawnBlocks();
        }
        return newRound;
      });
    } else {
      setMisses(m => m + 1);
      setScore(s => Math.max(0, s - 5));
    }
  };

  const handleComplete = () => {
    const avgReaction = reactionTimes.length > 0
      ? reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length
      : 0;
    const accuracy = (hits + misses) > 0 ? (hits / (hits + misses)) * 100 : 0;

    const energyEarned = Math.floor(score / 5) + Math.floor(accuracy / 10);
    addEnergy(energyEarned);

    completeLevel({
      levelId: 'color-identify',
      levelName: '红绿互补色块识别',
      score,
      accuracy: Math.max(0, accuracy),
      reactionTime: avgReaction,
      completedAt: new Date().toISOString(),
      contrast: contrastLevel,
      speed: speedLevel,
    });

    unlockLevel('memory-puzzle');
    unlockLevel('eye-exercise');
    navigate('/report');
  };

  const getBlockColor = (color: 'red' | 'green') => {
    if (color === 'red') return `rgba(239, 68, 68, ${contrastLevel})`;
    return `rgba(34, 197, 94, ${contrastLevel})`;
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-[#0a1a10] via-[#122a1a] to-[#1a3525] relative overflow-hidden">
      {/* Header */}
      <header className="relative z-10 px-4 pt-6 pb-2">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <button onClick={() => navigate('/')} className="text-white/60 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-base font-bold text-white">🔴🟢 红绿色块识别</h1>
          <div className="flex items-center gap-2">
            <Timer className="w-4 h-4 text-white/60" />
            <span className={`text-sm font-bold ${timeLeft <= 10 ? 'text-red-400' : 'text-white/80'}`}>{timeLeft}s</span>
          </div>
        </div>
        <div className="max-w-lg mx-auto mt-3">
          <div className="flex items-center justify-between text-xs text-white/50 mb-1">
            <span>第 {round}/{totalRounds} 轮</span>
            <span className="flex items-center gap-1"><Target className="w-3 h-3" /> {score}分</span>
          </div>
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-red-500 to-green-500 rounded-full"
              animate={{ width: `${(round / totalRounds) * 100}%` }}
            />
          </div>
        </div>
      </header>

      {/* Game Area */}
      <div className="relative z-10 px-4 mt-4">
        <div className="max-w-lg mx-auto">
          {gameState === 'ready' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16"
            >
              <div className="text-6xl mb-6">🔴🟢</div>
              <h2 className="text-xl font-bold text-white mb-3">红绿互补色块识别</h2>
              <p className="text-white/60 text-sm mb-2 max-w-xs mx-auto">
                屏幕上会出现红色和绿色的色块，它们会不断移动。
              </p>
              <p className="text-white/60 text-sm mb-8 max-w-xs mx-auto">
                根据提示快速点击指定颜色的色块！训练色觉敏感度和手眼协调。
              </p>
              <button onClick={startGame} className="btn-primary text-lg">
                <Zap className="w-5 h-5 inline mr-2" />开始训练
              </button>
            </motion.div>
          )}

          {gameState === 'playing' && (
            <div className="relative">
              {/* Target color indicator */}
              <div className="text-center mb-4">
                <span className="text-xs text-white/60">快速点击所有</span>
                <span className={`text-sm font-bold ml-2 ${targetColor === 'red' ? 'text-red-400' : 'text-green-400'}`}>
                  {targetColor === 'red' ? '🔴 红色' : '🟢 绿色'}
                </span>
                <span className="text-xs text-white/60 ml-1">色块</span>
              </div>

              {/* Game field */}
              <div className="relative w-full aspect-[4/5] max-w-[400px] mx-auto rounded-2xl border border-white/10 bg-black/30 overflow-hidden">
                {/* Background grid lines */}
                <div className="absolute inset-0 opacity-10">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="absolute w-full h-px bg-white" style={{ top: `${(i + 1) * 20}%` }} />
                  ))}
                </div>

                <AnimatePresence>
                  {blocks.map(block => block.visible && (
                    <motion.button
                      key={block.id}
                      className="absolute rounded-xl border-2 border-white/20 active:scale-75 transition-transform"
                      style={{
                        width: `${block.size}px`,
                        height: `${block.size}px`,
                        backgroundColor: getBlockColor(block.color),
                        boxShadow: `0 0 10px ${block.color === 'red' ? 'rgba(239,68,68,0.4)' : 'rgba(34,197,94,0.4)'}`,
                      }}
                      animate={{
                        left: `${block.x}%`,
                        top: `${block.y}%`,
                      }}
                      transition={{ type: 'spring', stiffness: 150, damping: 20 }}
                      onClick={() => handleBlockClick(block)}
                    />
                  ))}
                </AnimatePresence>

                {/* Feedback */}
                <AnimatePresence>
                  {feedback && (
                    <motion.div
                      initial={{ scale: 0.5, opacity: 1 }}
                      animate={{ scale: 2, opacity: 0 }}
                      exit={{ opacity: 0 }}
                      className="absolute text-2xl pointer-events-none"
                      style={{ left: `${feedback.x}%`, top: `${feedback.y}%` }}
                    >
                      {feedback.type === 'correct' ? '✓' : '✗'}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Result Modal */}
      <AnimatePresence>
        {showResult && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              className="game-card p-6 max-w-sm w-full text-center"
            >
              <div className="text-4xl mb-4">🎯</div>
              <h2 className="text-xl font-bold text-white mb-4">训练完成！</h2>
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-xs text-white/50">得分</p>
                  <p className="text-lg font-bold text-[#7dffa8]">{score}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-xs text-white/50">准确率</p>
                  <p className="text-lg font-bold text-[#fbbf24]">
                    {(hits + misses) > 0 ? Math.round((hits / (hits + misses)) * 100) : 0}%
                  </p>
                </div>
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-xs text-white/50">平均反应</p>
                  <p className="text-lg font-bold text-[#60a5fa]">
                    {reactionTimes.length > 0 ? Math.round(reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length) : 0}ms
                  </p>
                </div>
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-xs text-white/50">获得能量</p>
                  <p className="text-lg font-bold text-[#fbbf24]">
                    +{Math.floor(score / 5) + Math.floor(((hits) / Math.max(hits + misses, 1)) * 10)}
                  </p>
                </div>
              </div>
              <button onClick={handleComplete} className="btn-primary w-full">
                查看训练报告 📊
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
